<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('noteSpese.ors_key') }}
      </oxd-text>

      <oxd-divider />

      <oxd-text
        tag="p"
        :class="
          settings.openRouteServiceConfigured
            ? 'note-spese-ors-configured'
            : 'note-spese-ors-missing'
        "
      >
        {{
          settings.openRouteServiceConfigured
            ? $t('noteSpese.ors_configured')
            : $t('noteSpese.ors_missing')
        }}
      </oxd-text>

      <oxd-form :loading="isSavingKey" @submit-valid="onSaveKey">
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="orsKeyInput"
                type="password"
                :label="$t('noteSpese.ors_key')"
                :placeholder="orsKeyPlaceholder"
                :rules="rules.orsKey"
                autocomplete="new-password"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-actions>
          <oxd-button
            type="submit"
            display-type="secondary"
            :label="$t('general.save')"
          />
        </oxd-form-actions>
      </oxd-form>

      <oxd-divider />

      <oxd-form-row>
        <oxd-grid :cols="2" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              :model-value="settings.mobileUrl"
              :label="$t('noteSpese.mobile_url')"
              readonly
            />
          </oxd-grid-item>
          <oxd-grid-item class="note-spese-mobile-url-copy">
            <oxd-icon-button name="files" @click="onCopyMobileUrl" />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
    </div>

    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('noteSpese.directors_title') }}
      </oxd-text>

      <oxd-divider />

      <table v-if="directors.length > 0" class="note-spese-directors-table">
        <thead>
          <tr>
            <th>{{ $t('general.name') }}</th>
            <th>{{ $t('noteSpese.director_active') }}</th>
            <th class="note-spese-numeric">{{ $t('noteSpese.vehicles') }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <template v-for="director in directors" :key="director.empNumber">
            <tr>
              <td>{{ director.firstName }} {{ director.lastName }}</td>
              <td>
                <oxd-input-field
                  type="checkbox"
                  :model-value="director.active"
                  @update:model-value="onToggleActive(director, $event)"
                />
              </td>
              <td class="note-spese-numeric">{{ director.vehicleCount }}</td>
              <td>
                <oxd-button
                  display-type="ghost"
                  :label="$t('noteSpese.vehicles')"
                  @click="onToggleVehicles(director)"
                />
              </td>
            </tr>
            <tr v-if="expandedEmpNumber === director.empNumber">
              <td colspan="4">
                <director-vehicles :emp-number="director.empNumber" />
              </td>
            </tr>
          </template>
        </tbody>
      </table>

      <oxd-text v-else>{{ $t('general.no_records_found') }}</oxd-text>

      <oxd-divider />

      <oxd-form :loading="isAddingDirector" @submit-valid="onAddDirector">
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <employee-autocomplete
                v-model="newDirectorEmployee"
                :rules="rules.employee"
                :label="$t('general.employee')"
                :params="{includeEmployees: 'onlyCurrent'}"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-actions>
          <oxd-button
            type="submit"
            display-type="secondary"
            :label="$t('noteSpese.add_director')"
          />
        </oxd-form-actions>
      </oxd-form>
    </div>

    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('noteSpese.month_locks') }}
      </oxd-text>

      <oxd-divider />

      <month-locks />
    </div>
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import useToast from '@/core/util/composable/useToast';
import {required} from '@/core/util/validation/rules';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete';
import DirectorVehicles from '@/orangehrmNoteSpesePlugin/components/DirectorVehicles.vue';
import MonthLocks from '@/orangehrmNoteSpesePlugin/components/MonthLocks.vue';

export default {
  name: 'DirectorsAdmin',

  components: {
    'employee-autocomplete': EmployeeAutocomplete,
    'director-vehicles': DirectorVehicles,
    'month-locks': MonthLocks,
  },

  setup() {
    const toast = useToast();

    const directorsHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/note-spese/directors',
    );
    const settingsHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/note-spese/settings',
    );

    return {
      toast,
      directorsHttp,
      settingsHttp,
    };
  },

  data() {
    return {
      directors: [],
      settings: {
        openRouteServiceConfigured: false,
        mobileUrl: '',
      },
      orsKeyInput: '',
      isSavingKey: false,
      isAddingDirector: false,
      newDirectorEmployee: null,
      expandedEmpNumber: null,
    };
  },

  computed: {
    rules() {
      return {
        employee: [],
        orsKey: [required],
      };
    },

    orsKeyPlaceholder() {
      return this.settings.openRouteServiceConfigured ? '••••••••' : '';
    },
  },

  created() {
    this.fetchDirectors();
    this.fetchSettings();
  },

  methods: {
    async fetchDirectors() {
      const response = await this.directorsHttp.getAll();
      this.directors = response.data?.data ?? [];
    },

    async fetchSettings() {
      const response = await this.settingsHttp.getAll();
      this.settings = response.data?.data ?? this.settings;
    },

    async onSaveKey() {
      this.isSavingKey = true;
      try {
        const response = await this.settingsHttp.request({
          method: 'PUT',
          data: {openRouteServiceKey: this.orsKeyInput},
        });
        this.settings = response.data?.data ?? this.settings;
        this.orsKeyInput = '';
        this.toast.updateSuccess();
      } finally {
        this.isSavingKey = false;
      }
    },

    async onCopyMobileUrl() {
      try {
        await navigator.clipboard?.writeText(this.settings.mobileUrl);
      } catch (error) {
        // Niente permesso di scrittura sugli appunti: l'indirizzo resta
        // comunque visibile e selezionabile a mano nel campo qui sopra.
      }
    },

    async onToggleActive(director, active) {
      await this.directorsHttp.update(director.empNumber, {active});
      director.active = active;
      this.toast.updateSuccess();
    },

    onToggleVehicles(director) {
      this.expandedEmpNumber =
        this.expandedEmpNumber === director.empNumber
          ? null
          : director.empNumber;
    },

    async onAddDirector() {
      if (!this.newDirectorEmployee?.id) return;

      this.isAddingDirector = true;
      try {
        await this.directorsHttp.create({
          empNumber: this.newDirectorEmployee.id,
        });
        this.newDirectorEmployee = null;
        this.toast.addSuccess();
        await this.fetchDirectors();
      } finally {
        this.isAddingDirector = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.note-spese-ors-configured {
  color: #17a54a;
}

.note-spese-ors-missing {
  color: #d9534f;
}

.note-spese-mobile-url-copy {
  display: flex;
  align-items: center;
}

.note-spese-directors-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;

  th,
  td {
    text-align: left;
    padding: 0.5rem;
    border-bottom: 1px solid #e0e0e0;
  }
}

.note-spese-numeric {
  text-align: right;
}
</style>
